import { Icon, IconButton, Modal, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { dateMaskTrack, MaskInput } from '@hero/components/atoms';
import { OCCASION_TYPES } from '@hero/constants';
import { useDebounceEffect } from '@hero/hooks';
import { Table } from '@hero/molecules';
import { useUpdateContact } from '@hero/services/contact-service/hooks';

import * as S from './style';

function validateDate(dateString, format) {
  const date = dayjs(dateString, format);
  return date.isValid();
}

function FormattedDateEditInput({ value, onUpdate }) {
  const { t } = useTranslation();

  const [localValue, setLocalValue] = useState(value);

  function handleChange(e) {
    setLocalValue(e.target.value);
  }

  const isValid1 = validateDate(localValue, 'MM/DD/YYYY');
  const isValid2 = validateDate(localValue, 'MM/DD');
  const isValid = isValid1 || isValid2;

  useDebounceEffect(
    async () => {
      const isValid1 = validateDate(localValue, 'MM/DD/YYYY');
      const isValid2 = validateDate(localValue, 'MM/DD');
      if (isValid1 || isValid2) {
        onUpdate(localValue);
      }
    },
    1000,
    [localValue]
  );

  return (
    <MaskInput
      value={localValue}
      onChange={handleChange}
      mask={'mm/dd/yyyy'}
      track={dateMaskTrack}
      placeholder={t('dayAndMonthOrYearPlaceholder')}
      error={!isValid}
      replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
    />
  );
}

function MissingRecipientInfoModal({
  isOpen,
  onRequestClose,
  contacts,
  occasionType,
  deleteContact,
  disabledColumns
}) {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});

  const { mutate: updateContact } = useUpdateContact();

  const pageCount = useMemo(() => {
    return Math.ceil(contacts?.length / pageSize);
  }, [pageSize]);

  const contactsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    while (pageIndex < pageCount) {
      result[pageIndex] = contacts?.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
      pageIndex += 1;
    }

    return result;
  }, [contacts, pageSize]);

  const columns = React.useMemo(
    () =>
      [
        {
          id: 'email',
          header: t('email'),
          size: 120,
          accessorKey: 'email'
        },
        {
          header: t('firstName'),
          size: 100,
          accessorKey: 'firstName'
        },
        {
          header: t('lastName'),
          size: 100,
          accessorKey: 'lastName'
        },
        {
          id: 'birthday',
          header: t('birthday'),
          editable: true,
          type: 'date',
          accessorKey: 'birthday.full',
          componentProps: {
            excludeFuture: true,
            maxDate: new Date()
            // isDatePickerShown: false,
            // placeholder: 'MM/DD or MM/DD/YYYY'
          },
          cell: ({ row }) => {
            return (
              <FormattedDateEditInput
                value={row.original.birthday.full}
                onUpdate={value => {
                  updateContact({
                    id: row.original.id,
                    body: {
                      birthday: value
                    }
                  });
                }}
              />
            );
          }
        },
        {
          id: 'anniversary',
          header: t('workAnniversary'),
          editable: true,
          type: 'date',
          accessorKey: 'anniversary.full',
          isValidFn: val => val,
          componentProps: {
            excludeFuture: false
            // isDatePickerShown: false,
            // placeholder: 'MM/DD or MM/DD/YYYY'
          },
          cell: ({ row }) => {
            return (
              <FormattedDateEditInput
                value={row.original.anniversary.full}
                onUpdate={value => {
                  updateContact({
                    id: row.original.id,
                    body: {
                      anniversary: value
                    }
                  });
                }}
              />
            );
          }
        },
        {
          id: 'actions',
          header: '',
          size: 50,
          cell: ({ row }) => {
            return (
              <IconButton
                variant="text"
                color="error"
                onClick={() => {
                  deleteContact(row.id);
                }}
              >
                <Icon name="delete" size="lg" />
              </IconButton>
            );
          }
        }
      ].filter(column => !disabledColumns.includes(column.id)),
    []
  );

  const updateData = (originalRow, columnId, value) => {
    updateContact({
      id: originalRow.id,
      body: {
        [columnId]: value
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      primaryButtonProps={{
        children: t('done'),
        onClick: () => onRequestClose(false)
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
      title={t(
        occasionType === 'birthday' ? 'enterMissingBirthdays' : 'enterMissingWorkAnniversaries'
      )}
      size="lg"
    >
      <S.MissingInfoWrapper>
        <Typography as="div" variant="body1Bold" style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name="f-error" />
          <Trans
            i18nKey="amountOfRecipientMissingOccasionType"
            components={{ div: <Typography color="error" variant="body1Bold" /> }}
            values={{ amount: contacts?.length, type: OCCASION_TYPES[occasionType].toLowerCase() }}
          />
        </Typography>
        <Typography variant="body2" emphasis="weaker" style={{ marginBottom: 8 }}>
          {t('missingRecipientInfoModalDesc', { type: OCCASION_TYPES[occasionType].toLowerCase() })}
        </Typography>
        <Table
          columns={columns}
          data={contactsByPage[pageIndex] || []}
          state={{
            globalFilter: globalFilters
          }}
          updateData={updateData}
          setPagination={setPagination}
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          useGlobalFuzzyFilter
          manualPagination
          pagination={{
            pageIndex,
            pageSize,
            total: contacts?.length
          }}
          pageCount={pageCount}
          enabled={{ filter: false, header: true }}
        />
      </S.MissingInfoWrapper>
    </Modal>
  );
}

export { MissingRecipientInfoModal };
